import { useState, useEffect } from 'react';
import axios from 'axios';
import { Loader2, ArrowUpDown, Camera, Users, Crown, Check, TrendingUp, TrendingDown, CalendarClock } from 'lucide-react';
import { toast } from 'sonner';
import './CompareAll.css';

const API = process.env.REACT_APP_BACKEND_URL;

const BAND_LABEL = { g: 'On target', a: 'Watch', r: 'Action needed', n: 'No forecast' };

function PctPill({ pct, band }) {
  if (pct === null || pct === undefined) return <span className="ca-pct ca-pct-n">—</span>;
  return <span className={`ca-pct ca-pct-${band}`}>{pct}%</span>;
}

function PodiumCard({ r, place }) {
  return (
    <div className={`ca-podium-card ca-place-${place}`} style={{ animationDelay: `${place * 120}ms` }} data-testid={`ca-podium-${place}`}>
      <span className="ca-place-badge">{place}</span>
      <div className="ca-podium-photo">
        {r.photo
          ? <img src={`${API}${r.photo}`} alt={r.name} />
          : <span className="ca-initials">{r.name.split(' ').map((p) => p[0]).slice(0, 2).join('')}</span>}
        {place === 1 && <Crown size={20} className="ca-crown" />}
      </div>
      <div className="ca-podium-name">{r.name}</div>
      <div className="ca-podium-area">{r.area_label} · {r.team}</div>
      <div className={`ca-podium-pct ca-text-${r.band}`}>{r.hy_pct}%</div>
      <div className="ca-podium-sub">vs forecast</div>
    </div>
  );
}

function MoverItem({ r, onClick }) {
  const up = r.delta > 0;
  return (
    <button className="ca-mover-item" onClick={onClick} data-testid={`ca-mover-${r.row_ref}`}>
      {r.photo
        ? <img src={`${API}${r.photo}`} alt="" className="ca-face" />
        : <span className="ca-face ca-face-blank">{r.name.split(' ').map((p) => p[0]).slice(0, 2).join('')}</span>}
      <span className="ca-mover-name">{r.name}</span>
      <span className="ca-mover-journey">{r.q1_pct}% → {r.q2_pct}%</span>
      <span className={`ca-mover-delta ${up ? 'is-up' : 'is-down'}`}>
        {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {up ? '+' : ''}{r.delta} pts
      </span>
    </button>
  );
}

export default function CompareAll({ onOpenReport }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [group, setGroup] = useState('photographer');
  const [areaFilter, setAreaFilter] = useState('all');
  const [sortMode, setSortMode] = useState('best');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/staff/compare`);
        setData(r.data);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load comparison');
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="ca-loading"><Loader2 size={20} className="ca-spin" /> Loading comparison…</div>;
  if (!data) return null;

  const areaOptions = [...new Map(data.rows.map((r) => [r.area, r.area_label]))];
  const filtered = areaFilter === 'all' ? data.rows : data.rows.filter((r) => r.area === areaFilter);
  const filterLabel = areaFilter === 'all' ? 'All' : (areaOptions.find(([k]) => k === areaFilter)?.[1] || areaFilter);
  const teams = areaFilter === 'all' ? data.teams : data.teams.filter((t) => t.area === areaFilter);

  const counts = filtered.reduce((acc, r) => { acc[r.band] = (acc[r.band] || 0) + 1; return acc; }, {});
  const withPct = filtered.filter((r) => r.hy_pct !== null);
  const avg = withPct.length ? Math.round(withPct.reduce((s, r) => s + r.hy_pct, 0) / withPct.length) : null;

  const bestOrder = [...filtered].sort((a, b) => (a.hy_pct === null) - (b.hy_pct === null) || (b.hy_pct ?? 0) - (a.hy_pct ?? 0));
  const rankMap = new Map(bestOrder.map((r, i) => [r.row_ref, i + 1]));
  const podium = bestOrder.filter((r) => r.hy_pct !== null).slice(0, 3);
  const due = filtered.filter((r) => !r.met).length;

  const movers = filtered
    .filter((r) => r.q1_pct !== null && r.q2_pct !== null)
    .map((r) => ({ ...r, delta: r.q2_pct - r.q1_pct }));
  const climbers = movers.filter((m) => m.delta > 0).sort((a, b) => b.delta - a.delta).slice(0, 3);
  const fallers = movers.filter((m) => m.delta < 0).sort((a, b) => a.delta - b.delta).slice(0, 3);

  let rows = bestOrder;
  if (sortMode === 'worst') rows = [...bestOrder].reverse();
  if (sortMode === 'az') rows = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  if (sortMode === 'grade') rows = [...filtered].sort((a, b) => (parseInt(String(b.grade).split('>').pop()) || 0) - (parseInt(String(a.grade).split('>').pop()) || 0));

  return (
    <div className="ca-wrap" data-testid="compare-all">
      <div className="ca-toolbar">
        <div className="ca-group-tabs" data-testid="ca-group-tabs">
          <button className={group === 'photographer' ? 'is-active' : ''} onClick={() => setGroup('photographer')} data-testid="ca-group-photographer">
            <Camera size={13} /> Photographers
          </button>
          <button className={group === 'team' ? 'is-active' : ''} onClick={() => setGroup('team')} data-testid="ca-group-team">
            <Users size={13} /> Area Managers
          </button>
          <button className={group === 'director' ? 'is-active' : ''} onClick={() => setGroup('director')} data-testid="ca-group-director">
            <Crown size={13} /> Regional Directors
          </button>
        </div>
        {group !== 'director' && (
          <div className="ca-area-pills" data-testid="ca-area-pills">
            <button className={areaFilter === 'all' ? 'is-active' : ''} onClick={() => setAreaFilter('all')} data-testid="ca-area-all">
              All areas
            </button>
            {areaOptions.map(([k, label]) => (
              <button key={k} className={areaFilter === k ? 'is-active' : ''} onClick={() => setAreaFilter(k)} data-testid={`ca-areafilter-${k}`}>
                {label}
              </button>
            ))}
          </div>
        )}
        {group === 'photographer' && (
          <button className="ca-sort-btn" data-testid="ca-sort-btn"
            onClick={() => setSortMode((s) => ({ best: 'worst', worst: 'az', az: 'grade', grade: 'best' }[s]))}>
            <ArrowUpDown size={13} />
            {{ best: 'Best first', worst: 'Worst first', az: 'A–Z', grade: 'By grade' }[sortMode]}
          </button>
        )}
      </div>

      {group === 'photographer' && (
        <div className="ca-board">
          <div className="ca-head-row">
            <span className="ca-title">{data.period} — {filterLabel === 'All' ? 'All Photographers' : `${filterLabel} Photographers`}</span>
            <div className="ca-summary">
              <span className="ca-chip">{filtered.length} photographers</span>
              <span className="ca-chip ca-chip-g">{counts.g || 0} on target</span>
              <span className="ca-chip ca-chip-a">{counts.a || 0} watch</span>
              <span className="ca-chip ca-chip-r">{counts.r || 0} action</span>
              {avg !== null && <span className="ca-chip ca-chip-avg">avg {avg}%</span>}
              {due > 0
                ? <span className="ca-chip ca-chip-due" data-testid="ca-overdue-chip"><CalendarClock size={11} /> {due} catch-up{due === 1 ? '' : 's'} due</span>
                : <span className="ca-chip ca-chip-g" data-testid="ca-allmet-chip"><Check size={11} /> All caught up</span>}
            </div>
          </div>

          {(climbers.length > 0 || fallers.length > 0) && (
            <div className="ca-movers" data-testid="ca-movers">
              <div className="ca-mover-panel ca-mover-up">
                <div className="ca-mover-title"><TrendingUp size={14} /> Biggest climbers <em>Q1 → Q2</em></div>
                {climbers.length > 0
                  ? climbers.map((r) => <MoverItem key={r.row_ref} r={r} onClick={() => onOpenReport(r.staff_id || r.row_ref)} />)
                  : <p className="ca-mover-none">No climbers this quarter</p>}
              </div>
              <div className="ca-mover-panel ca-mover-down">
                <div className="ca-mover-title ca-title-down"><TrendingDown size={14} /> Biggest fallers <em>Q1 → Q2</em></div>
                {fallers.length > 0
                  ? fallers.map((r) => <MoverItem key={r.row_ref} r={r} onClick={() => onOpenReport(r.staff_id || r.row_ref)} />)
                  : <p className="ca-mover-none">No fallers this quarter</p>}
              </div>
            </div>
          )}

          <div className="ca-podium">
            {podium[1] && <PodiumCard r={podium[1]} place={2} />}
            {podium[0] && <PodiumCard r={podium[0]} place={1} />}
            {podium[2] && <PodiumCard r={podium[2]} place={3} />}
          </div>

          <table className="ca-table">
            <thead>
              <tr>
                <th className="ca-th-rank">#</th>
                <th className="ca-th-name">Photographer</th>
                <th>Area</th>
                <th>Grade</th>
                <th>2026 forecast</th>
                <th>2026 actual</th>
                <th className="ca-th-bar">vs forecast</th>
                <th>Q2</th>
                <th>Status</th>
                <th>Catch-up</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.row_ref} className={`ca-row ca-row-${r.band}`}
                  onClick={() => onOpenReport(r.staff_id || r.row_ref)}
                  title="Open photographer report"
                  data-testid={`ca-row-${rankMap.get(r.row_ref)}`}
                >
                  <td className="ca-rank">{rankMap.get(r.row_ref)}</td>
                  <td className="ca-name">
                    {r.photo
                      ? <img src={`${API}${r.photo}`} alt="" className="ca-face" />
                      : <span className="ca-face ca-face-blank">{r.name.split(' ').map((p) => p[0]).slice(0, 2).join('')}</span>}
                    {r.name}
                    {r.has_card && <span className="ca-card-dot" title="Performance card available" />}
                  </td>
                  <td className="ca-area">{r.area_label}</td>
                  <td className="ca-grade">{r.grade}</td>
                  <td className="ca-money ca-dim">{r.f2026}</td>
                  <td className="ca-money">{r.a2026}</td>
                  <td className="ca-barcell">
                    <div className="ca-bar-track">
                      <div className={`ca-bar-fill ca-fill-${r.band}`} style={{ width: `${r.hy_pct === null ? 0 : Math.max(3, Math.min(100, (r.hy_pct / 150) * 100))}%` }} />
                    </div>
                  </td>
                  <td><PctPill pct={r.hy_pct} band={r.band} /></td>
                  <td><span className={`ca-status ca-status-${r.band}`}>{BAND_LABEL[r.band] || '—'}</span></td>
                  <td>
                    {r.met
                      ? <span className="ca-met" title="Meeting record saved this quarter"><Check size={12} /> Done</span>
                      : <span className="ca-due" title="No meeting record saved this quarter yet">Due</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {data.areas_missing.length > 0 && (
            <p className="ca-missing" data-testid="ca-missing">
              Covering {data.areas_covered.join(', ')} so far — awaiting reports for {data.areas_missing.join(', ')}. Send them over and everyone joins this table automatically.
            </p>
          )}
        </div>
      )}

      {group === 'team' && (
        <div className="ca-board">
          <div className="ca-head-row">
            <span className="ca-title">{data.period} — Team totals by Area Manager</span>
          </div>
          <div className="ca-teams">
            {teams.map((t) => (
              <div key={t.team} className={`ca-team-card ca-team-${t.band}`} data-testid={`ca-team-${t.area}`}>
                <div className="ca-team-name">{t.team}</div>
                <div className="ca-team-area">{t.area_label}</div>
                <div className="ca-team-grid">
                  <div><span>2026 forecast</span><b>{t.f2026}</b></div>
                  <div><span>2026 actual</span><b>{t.a2026}</b></div>
                  <div><span>vs forecast</span><b className={`ca-text-${t.band}`}>{t.totals_pct !== null ? `${t.totals_pct}%` : '—'}</b></div>
                </div>
              </div>
            ))}
          </div>
          <p className="ca-missing">Each team rolls up to its Area Manager. More teams appear here as their traffic light reports are uploaded ({data.areas_missing.join(', ')} pending).</p>
        </div>
      )}

      {group === 'director' && (
        <div className="ca-board">
          <div className="ca-empty" data-testid="ca-director-empty">
            <Crown size={26} />
            <h4>Regional Director comparison</h4>
            <p>This unlocks once at least two areas have traffic light reports — each RD's regions will be rolled up and compared side by side. Currently covering: {data.areas_covered.join(', ')}.</p>
          </div>
        </div>
      )}
    </div>
  );
}
